import { UartOptions } from '@app/typedef/define/uart/UartOptions';
import { UartPortInfo, USB_VENDOR_NAMES } from '@app/typedef/define/uart/UartPort';

/*
 * 展示用的格式化。纯函数，没有 Angular 依赖。
 *
 * 这里**只产出与语言无关的串**（ID、符号、数字）—— 需要翻译的文案归组件管，
 * 免得一个 util 里混进 i18n key，调用方还得猜哪些返回值是要过 translate 的。
 */

/**
 * USB 标识 → `0x1A86:0x7523`。
 *
 * 只有一个存在时另一个显示为 `0x????`；**两个都没有则返回 null**（板载 UART 就是这种情况），
 * 由调用方决定说什么 —— 通常是「未知设备」之类需要翻译的文案。
 */
export function formatUsbId(info: UartPortInfo): string | null {
  if (info.usbVendorId == null && info.usbProductId == null) return null;
  return `${hex4(info.usbVendorId)}:${hex4(info.usbProductId)}`;
}

/**
 * USB 厂商 ID → 厂名，认不出返回 null。
 *
 * 厂名是专有名词，**不进 i18n**（`沁恒 WCH` 在任何语言下都这么写）。
 */
export function usbVendorName(info: UartPortInfo): string | null {
  const vid = info.usbVendorId;
  if (vid == null) return null;
  return USB_VENDOR_NAMES[vid] ?? null;
}

/**
 * 端口的显示名 → `0x1A86:0x7523 · 沁恒 WCH`。
 *
 * 表头那颗端口按钮、端口浮层里的每一行、以及导出的表头都要这一串，所以拼法放这里一份 ——
 * 各写一遍的话，改其中一处忘了另一处是迟早的事。
 *
 * 三种情况：
 * - ID 与厂名都有 → `0x1A86:0x7523 · 沁恒 WCH`
 * - 厂名认不出 → 只剩 ID（不留一个孤零零的分隔符）
 * - **没有 USB 标识（板载 UART）→ null**，由调用方去翻译「未知设备」那条文案
 *
 * 第三种不需要再看厂名：`formatUsbId` 只在**两个 ID 都缺**时给 null，而 `usbVendorName`
 * 要的正是 `usbVendorId` —— 前者为 null 就意味着后者也必为 null，所以这里直接返回。
 *
 * 浮层里一行只有一列，要的就是拼好的这一串。**要拆成两个字段**（比如左边 ID、右边厂名
 * 各给一种字色）的地方仍应分别调上面两个函数 —— 它们就是为此留着的。
 */
export function describePort(info: UartPortInfo): string | null {
  const id = formatUsbId(info);
  if (id === null) return null;
  const vendor = usbVendorName(info);
  return vendor === null ? id : `${id} · ${vendor}`;
}

/**
 * 参数摘要 → `115200 8-N-1`。
 *
 * 用 8-N-1 这套串口行业通用的记法（数据位-校验位-停止位，校验位取首字母
 * N/O/E = None/Odd/Even），比写「8 位数据 无校验 1 位停止」更短且不分语言。
 * 硬件流控额外缀一个 `(RTS/CTS)`。
 */
export function describeOptions(options: UartOptions): string {
  const parity = { none: 'N', odd: 'O', even: 'E' }[options.parity];
  const base = `${options.baudRate} ${options.dataBits}-${parity}-${options.stopBits}`;
  return options.flowControl === 'hardware' ? `${base} (RTS/CTS)` : base;
}

/** 毫秒时间戳 → `14:03:22.417`。收发记录每行前面的那个时间。 */
export function formatTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number, width = 2) => String(n).padStart(width, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function hex4(value: number | undefined): string {
  return value == null ? '0x????' : `0x${value.toString(16).padStart(4, '0').toUpperCase()}`;
}
